import { FC, useEffect, useMemo, useState } from 'react';

import { useTour } from '@reactour/tour';
import { useFormikContext } from 'formik';
import { useFormWarning } from 'hooks';
import { compact, isEqual, startCase, uniqBy, upperCase } from 'lodash';
import { useGetAccountUsers, useGetLeads } from 'modules/account/contact/hooks';
import { useDropzone } from 'react-dropzone';
import { BsPlusLg } from 'react-icons/bs';
import { useLocation } from 'react-router-dom';
import { Button } from 'ui/components/common';
import {
  Autocomplete,
  DateField,
  Input,
  Radio,
  RadioGroup,
  TagsField,
  Textarea,
} from 'ui/components/form';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import SelectAvatarPopover from 'ui/components/form/SelectAvatar/SelectAvatarPopover';
import UploadListItem from 'ui/partials/Composer/UploadListItem';
import { toastError } from 'ui/partials/Toast';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import {
  DEMO_ACCOUNT_USER,
  DEMO_CONTACT,
  SIZE,
  TASK_PRIORITY,
} from 'utils/constants';
import { cn, convertToMegabytes } from 'utils/helpers';

import { TASK_FORM } from './schema';

const types = [
  { label: 'TO DO', value: 'TODO' },
  { label: 'EMAIL', value: 'EMAIL' },
  { label: 'CALL', value: 'CALL' },
];

const TaskForm: FC<{ isLoading?: boolean }> = ({ isLoading = false }) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setValues,
    initialValues,
  } = useFormikContext<TASK_FORM>();

  const { onBack } = useFormWarning({
    isDirty: !isEqual(initialValues, values),
    item: 'task',
  });

  const { isOpen } = useTour();

  const [accountUserKeyword, setAccountUserKeyword] = useState('');
  const [leadKeyword, setLeadKeyword] = useState('');
  const { data: leads } = useGetLeads({ keyword: leadKeyword });
  const { data: accountUsers } = useGetAccountUsers({
    keyword: accountUserKeyword,
  });
  const location = useLocation();

  const inLeadsTasksSection = useMemo(() => {
    return (
      location.pathname.includes('leads') && location.pathname.includes('tasks')
    );
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      setValues({
        priority: 'HIGH',
        dueAt: new Date(),
        leadId: DEMO_CONTACT.value,
        title: 'How to create a Task',
        type: 'TODO',
        accountUserId: [DEMO_ACCOUNT_USER.value],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    maxSize: 1024 * 1024 * 5,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      setFieldValue(
        'attachments',
        uniqBy([...(values?.attachments ?? []), ...acceptedFiles], 'name'),
      );

      const sizeToUpload: number =
        values?.attachments?.reduce(
          (acc: number, curr: any) => acc + curr.size,
          0,
        ) ?? 0;

      if (sizeToUpload > 1024 * 1024 * 5) {
        toastError(`file ${convertToMegabytes(sizeToUpload)} to large.`);
      }
    },
    onDropRejected: (fileRejections) => {
      const totalSize = fileRejections.reduce(
        (acc, { errors: fileErrors, file }) => {
          if (fileErrors[0].code === 'file-too-large') {
            return acc + file.size;
          }
          return acc;
        },
        0,
      );
      if (fileRejections[0].errors[0].code === 'file-too-large') {
        toastError(`file ${convertToMegabytes(totalSize)} to large.`);
      }
    },
  });

  const accountUserItems = useMemo(() => {
    if (isOpen) {
      return [DEMO_ACCOUNT_USER];
    }

    return (
      accountUsers?.data?.map((accountUser) => ({
        photoUrl: accountUser.photoUrl,
        itemLabel: accountUser.fullName,
        description: accountUser.emailAddress,
        value: accountUser._id,
      })) ?? ([] as AvatarOption[])
    );
  }, [accountUsers?.data, isOpen]);

  const handleRemoveAttachment = (name: string) => {
    const attachments = [...(values?.attachments ?? [])].filter(
      (attachment: any) => attachment.name !== name,
    );

    setFieldValue('attachments', attachments);
  };

  const filterTaskType = (inputValue: string) => {
    return types.filter((i) =>
      i.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };
  const loadTaskTypeOptions = (
    inputValue: string,
    callback: (options: typeof types) => void,
  ) => {
    setTimeout(() => {
      callback(filterTaskType(inputValue));
    }, 1000);
  };

  const contactItems = useMemo(() => {
    if (isOpen) {
      return [DEMO_CONTACT];
    }

    return (
      leads?.data.map((lead) => ({
        photoUrl: lead.photoUrl,
        itemLabel: lead.fullName,
        description: lead.emailAddress,
        value: lead.id,
      })) ?? ([] as AvatarOption[])
    );
  }, [isOpen, leads?.data]);

  const defaultContacts = useMemo(() => {
    if (isOpen) {
      return [DEMO_CONTACT];
    }

    return contactItems?.filter((i) => values.leadId?.includes(i.value));
  }, [contactItems, isOpen, values.leadId]);

  const defaultAccountUsers = useMemo(() => {
    if (isOpen) {
      return [DEMO_ACCOUNT_USER];
    }

    return accountUserItems?.filter((i) =>
      values.accountUserId?.includes(i.value),
    );
  }, [accountUserItems, isOpen, values.accountUserId]);

  return (
    <form onSubmit={handleSubmit}>
      <div
        className="space-y-7"
        data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.FORM.FORM}
      >
        <div className="flex justify-between gap-x-7">
          <Input
            className="w-full"
            type="text"
            name="title"
            size={SIZE.Medium}
            textType={errors.title && touched.title ? 'danger' : 'default'}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.title}
            placeholder="Input title"
            subText={errors.title && touched.title ? errors.title : ''}
            label="Title"
            disabled={isSubmitting}
            required
          />
          <DateField
            selected={values.dueAt}
            onChange={(_, value) =>
              setFieldValue('dueAt', new Date(value as Date))
            }
            placeholder="Select"
            name="dueAt"
            size={SIZE.Medium}
            label="Due Date"
            disabled={isSubmitting}
            required
          />
        </div>
        <div className="flex gap-x-7">
          <Autocomplete
            className="w-full max-w-[258px]"
            name="type"
            size={SIZE.Medium}
            state={errors.type && touched.type ? 'danger' : undefined}
            onChange={(val: any) => setFieldValue('type', val.value)}
            defaultValue={[
              {
                label: startCase(upperCase(values.type)),
                value: values.type,
              },
            ]}
            placeholder="Select"
            subText={errors.type && touched.type ? errors.type : ''}
            label="Type"
            required
            loadOptions={loadTaskTypeOptions}
            isDisabled={isSubmitting}
            isClearable={false}
          />

          <div>
            <h4 className="mb-2 text-sm">Priority</h4>
            <RadioGroup groupClassName="flex gap-3 flex-wrap required">
              {TASK_PRIORITY.map((p) => (
                <Radio
                  name="priority"
                  label={p.label}
                  value={p.value}
                  key={p.value}
                  checked={p.value === values.priority}
                  defaultChecked={p.value === values.priority}
                  onClick={() => setFieldValue('priority', p.value)}
                />
              ))}
            </RadioGroup>
          </div>
        </div>
        <div className="flex justify-between gap-x-7">
          {!inLeadsTasksSection ? (
            <div className="flex-1">
              <SelectAvatarPopover
                {...(errors.leadId && { state: 'danger' })}
                label="Associated Contact "
                subText={errors.leadId ? errors.leadId : undefined}
                selected={compact(defaultContacts)}
                size={SIZE.Small}
                values={contactItems}
                required
                onInputChange={(val) => setLeadKeyword(val)} // when searching
                onSelect={(val) => setFieldValue('leadId', val)} // when selecting from options
                onClickSelected={() => setFieldValue('leadId', null)} // when clicking a selected
                onClear={() => setFieldValue('leadId', null)} // when clicking X button
                placeholder="Select Contact"
              />
            </div>
          ) : null}
          <div className="flex-1">
            <SelectAvatarPopover
              {...(errors.accountUserId && { state: 'danger' })}
              label="Assign To "
              subText={errors.accountUserId ? errors.accountUserId : undefined}
              required
              selected={compact(defaultAccountUsers)}
              size={SIZE.Small}
              values={accountUserItems}
              onInputChange={(val) => setAccountUserKeyword(val)} // when searching
              onSelect={(val) =>
                setFieldValue(
                  'accountUserId',
                  values.accountUserId?.concat(val),
                )
              }
              onClickSelected={(selected) =>
                setFieldValue(
                  'accountUserId',
                  values.accountUserId?.filter((value) => value !== selected),
                )
              }
              onClear={() => setFieldValue('accountUserId', [])} // when clicking X button
              placeholder="Select"
            />
          </div>
        </div>
        <div>
          <TagsField
            className="w-full"
            onChange={(val) =>
              setFieldValue(
                'tagId',
                val?.map((tag) => tag.value),
              )
            }
            defaultValue={compact(values.tagId)}
          />
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold">Description</p>
          <Textarea
            richText
            className="rounded-[6px] border-[#DCECF7]"
            value={values.content}
            onChange={(val) => setFieldValue('content', val)}
          />
        </div>
        <div {...getRootProps()}>
          <Button
            intent={'tertiary'}
            label="UPLOAD ATTACHMENT"
            onClick={open}
            icon={BsPlusLg}
            left
          />
          <input {...getInputProps()} />
          {isDragActive && (
            <div
              className={cn(
                'absolute inset-y-0 left-0',
                'bg-white',
                'bg-opacity-90',
                'h-full w-full',
                'grid place-items-center',
                'border-2 border-dashed border-secondary',
              )}
            >
              <div className="text-lg">Drop Files Here</div>
            </div>
          )}
          <div className="mt-3 max-h-[164px] space-y-1 overflow-y-auto">
            {/**TODO: Type this properly */}
            {values?.attachments?.map((file: any) => (
              <UploadListItem
                key={file.name}
                showSize
                file={{
                  ...file,
                  fileName: file.name,
                  fileType: file.type,
                  fileSize: file.size,
                }}
                size={SIZE.Large}
                onRemove={() => handleRemoveAttachment(file.name)}
                isUploaded={false}
              />
            ))}
          </div>
        </div>
      </div>
      <div
        className={cn(
          'flex justify-end gap-3',
          'px-2.5 pt-5 mt-5',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          data-tut={TOUR_SELECTOR.TOUR.SALES.TASK.FORM.SAVE_BUTTON}
          type="submit"
          size={SIZE.Small}
          label="CREATE TASK"
          className="primary sm"
          disabled={isSubmitting || isLoading}
        />
        <Button
          onClick={onBack}
          label="CANCEL"
          size={SIZE.Small}
          intent={'tertiary'}
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
};

export default TaskForm;
